import {useState, useEffect} from "react";
import {CustomerManagerInterface} from "../../interfaces/customer/CustomerManagerInterface";

interface ManagerEditorProps {
    manager: CustomerManagerInterface | null;
    onSave: (manager: CustomerManagerInterface) => void;
    onClose: () => void;
}

export default function ManagerEditor({manager, onSave, onClose}: ManagerEditorProps) {
    const [formData, setFormData] = useState<Partial<CustomerManagerInterface>>({
        name: '',
        email: '',
        phone: '',
        is_active: true,
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<{[key: string]: string}>({});

    useEffect(() => {
        if (manager) {
            setFormData({
                ...manager,
                password: ''
            });
        } else {
            setFormData({
                name: '',
                email: '',
                phone: '',
                is_active: true,
                password: ''
            });
        }
    }, [manager]);

    const validateForm = (): boolean => {
        const newErrors: {[key: string]: string} = {};

        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = 'ФИО обязательно для заполнения';
        }

        if (!formData.email || formData.email.trim() === '') {
            newErrors.email = 'Email обязателен для заполнения';
        }

        if (!manager?.id && (!formData.password || formData.password === '')) {
            newErrors.password = 'Не указан пароль';
        }

        if (formData.password && formData.password.length > 0) {
            if (formData.password.length < 6) {
                newErrors.password = 'Пароль должен быть не менее 6 символов';
            } else if (!/\d/.test(formData.password) || !/[A-z]/.test(formData.password)) {
                newErrors.password = 'Пароль должен содержать как минимум одну букву и одну цифру';
            } else if (/[А-я]+/.test(formData.password)) {
                newErrors.password = 'Пароль не должен включать русские буквы';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        onSave(formData as CustomerManagerInterface);
    };

    const handleChange = (field: string, value: any) => {
        setFormData({
            ...formData,
            [field]: value
        });
        // Очищаем ошибку для этого поля
        if (errors[field]) {
            setErrors({
                ...errors,
                [field]: ''
            });
        }
    };

    return (
        <div className="modal show" onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="d-flex flex-column modal-content">
                    <div className="d-flex flex-column modal-box modal-header">
                        <button type="button" className="btn-close"
                                aria-label="Закрыть" onClick={onClose}></button>
                        <div className="fw-bold modal-title">
                            {manager?.id ? 'Редактирование менеджера' : 'Добавление менеджера'}
                        </div>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="d-flex flex-column modal-box modal-body">
                            <div className="form-control-item">
                                <label htmlFor="name">ФИО *</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    id="name"
                                    placeholder="ФИО"
                                    value={formData.name || ''}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>

                            <div className="form-control-item">
                                <label htmlFor="email">Email *</label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="email"
                                    placeholder="Email"
                                    value={formData.email || ''}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                />
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            </div>

                            <div className="form-control-item">
                                <label htmlFor="phone">Телефон</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="phone"
                                    placeholder="Телефон"
                                    value={formData.phone || ''}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                            </div>

                            {(showPassword || !manager?.id) && (
                                <div className="form-control-item">
                                    <label htmlFor="password">
                                        Пароль {!manager?.id && '*'}
                                    </label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                        id="password"
                                        placeholder="пароль (от 6 символов, латинские буквы + цифры)..."
                                        value={formData.password || ''}
                                        onChange={(e) => handleChange('password', e.target.value)}
                                    />
                                    {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                                </div>
                            )}

                            {manager?.id && !showPassword && (
                                <div className="form-control-item text-center text-underline-dashed">
                                    <a href="#" onClick={(e) => {
                                        e.preventDefault();
                                        setShowPassword(true);
                                    }}>
                                        изменить пароль
                                    </a>
                                </div>
                            )}

                            <div className="form-control-item">
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="is_active"
                                        checked={formData.is_active || false}
                                        onChange={(e) => handleChange('is_active', e.target.checked)}
                                    />
                                    <div className="form-check-icon"></div>
                                    <span className="form-check-text">
                                        Активный
                                    </span>
                                </label>
                            </div>
                        </div>
                        <div className="d-flex flex-column modal-box mt-3">
                            <button type="submit" className="btn btn-danger btn-lg ">
                                <i className="fa fa-save"></i> Сохранить
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}